import { useMemo } from 'react'
import { Link } from 'react-router-dom'
import { Correct } from '../../components/Correct'
import { addDays, AGENDA_DAYS, dayKey, dayLabel, formatTime, groupByDay, isAllDay, parseDayKey, sameDay, startOfDay } from '../../lib/calendar'
import { calloutsFor, externalContacts, kindOf, PERSONA_WORDS } from '../../lib/meetingCallouts'
import type { MeetingView } from '../../types/calendar'
import { CalloutList, chipClass, swatchStyle } from './MeetingChip'

// The agenda: the days in order, scrollable, each meeting a row with its time, the room, and every callout
// in words. Client meetings carry their account's colour; colleague-only and solo entries sit quietly.

export function AgendaView({ anchor, meetings, now, selectedId, onPick, onPrep }: {
  anchor: Date; meetings: MeetingView[]; now: Date; selectedId: string | null; onPick: (m: MeetingView) => void; onPrep: (m: MeetingView) => void
}) {
  const from = startOfDay(anchor)
  const to = addDays(from, AGENDA_DAYS)
  const days = useMemo(() => groupByDay(meetings.filter((m) => { const s = new Date(m.meeting.start); return s >= from && s < to }).map((m) => ({ start: m.meeting.start, view: m }))), [meetings, from.getTime(), to.getTime()])

  if (days.length === 0) return <div className="card cal-empty"><p className="muted">Nothing on the calendar between {dayKey(from)} and {dayKey(addDays(to, -1))}.</p></div>

  return (
    <div className="cal-agenda">
      {days.map(([key, items]) => {
        const day = parseDayKey(key) ?? new Date(key)
        const views = items.map((x) => x.view)
        const clients = views.filter((m) => kindOf(m) === 'client').length
        return (
          <section key={key} className={`cal-day ${sameDay(day, now) ? 'today' : ''}`}>
            <h2 className="cal-day-head">
              <span>{dayLabel(day, now)}</span>
              <span className="muted small">{views.length} meeting{views.length === 1 ? '' : 's'}{clients ? ` · ${clients} with clients` : ''}</span>
            </h2>
            <ul className="plain cal-rows">
              {views.map((m) => <AgendaRow key={m.meeting.meetingId} m={m} now={now} selected={m.meeting.meetingId === selectedId} onPick={onPick} onPrep={onPrep} />)}
            </ul>
          </section>
        )
      })}
    </div>
  )
}

function AgendaRow({ m, now, selected, onPick, onPrep }: { m: MeetingView; now: Date; selected: boolean; onPick: (m: MeetingView) => void; onPrep: (m: MeetingView) => void }) {
  const e = m.meeting
  const kind = kindOf(m)
  const callouts = calloutsFor(m, now)
  const outside = externalContacts(m)
  const unknown = Math.max(0, (m.facts?.externalAttendees ?? 0) - outside.length)
  const where = e.isOnline || /^https?:\/\//i.test(e.location ?? '') ? 'online' : (e.location ?? '')
  return (
    <li className={chipClass(m, selected, 'cal-row')} style={swatchStyle(m)} onClick={() => onPick(m)} aria-selected={selected}>
      <div className="cal-row-when">
        {isAllDay(e.start, e.end) ? <span className="cal-row-start">All day</span> : <><span className="cal-row-start">{formatTime(e.start)}</span><span className="muted small">{formatTime(e.end)}</span></>}
      </div>
      <div className="cal-row-body">
        <div className="cal-row-title">
          <strong>{e.subject || '(no subject)'}</strong>
          <span className={`tag kind kind-${kind}`}>{kind === 'client' ? 'client' : kind === 'external' ? 'outside' : kind === 'internal' ? 'internal' : 'just you'}</span>
          {e.isCancelled && <span className="tag sig low">cancelled</span>}
        </div>
        <div className="cal-row-meta">
          {m.companyName && e.companyId && <Link className="chip on" to={`/accounts/${encodeURIComponent(e.companyId)}`} onClick={(ev) => ev.stopPropagation()}>{m.companyName}</Link>}
          {e.companyId && <Correct kind="meeting-account" entityId={e.meetingId} entityLabel={e.subject || m.companyName || 'this meeting'} currentValue={`company:${e.companyId}`} compact />}
          {outside.slice(0, 3).map((c) => (
            <span key={c.personId} className={`chip persona-${c.persona ?? 'none'}`} title={[c.title, c.persona ? PERSONA_WORDS[c.persona] : null].filter(Boolean).join(' · ')}>
              {c.persona && <i className="persona-dot" />}{c.name}
            </span>
          ))}
          {outside.length > 3 && <span className="chip">+{outside.length - 3}</span>}
          {unknown > 0 && <span className="muted small">{unknown} outside attendee{unknown === 1 ? '' : 's'} not in your contacts</span>}
          {outside.length === 0 && unknown === 0 && e.attendeeEmails.length > 0 && <span className="muted small">{e.attendeeEmails.length} attendee{e.attendeeEmails.length === 1 ? '' : 's'}</span>}
          {where && <span className="muted small">{where}</span>}
        </div>
        <CalloutList callouts={callouts} onPrep={() => onPrep(m)} />
      </div>
    </li>
  )
}
